import React, { useEffect, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { createClassroom, updateClassroom } from "../../../../../services/ClassroomService.js";
import { getBranches } from "../../../../../services/BranchService.js";
import AsyncPagedSelect from "../../components/AsyncPagedSelect.jsx";
import PopupDone, { usePopupDone } from "../../../../components/common/PopupDone/PopupDone.jsx";

/* The kit's form dialog, not the classroom app's own Modal: .pg-modal
   for the frame, .pg-modal-panel--form for a body that scrolls under a
   fixed head and foot, .pg-field / .pg-label / .pg-input for the
   controls, and the save button in the footer rather than loose at the
   end of the form.

   What this replaced was a bare <form> of unlabelled <input>s inside
   .cls-frow, with an inline style on the checkbox and Save as the last
   thing in the scroll. */

const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const LabFormModal = ({ lab, onClose, onSaved }) => {
  const [form, setForm] = useState({
    classroomNo: lab?.classroomNo || "",
    branch: lab?.branch || "",
    floor: lab?.floor || "",
    capacity: lab?.capacity ?? "",
    description: lab?.description || "",
    outOfOrder: lab ? lab.isActive === false : false,
    reason: lab?.reason || "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Mounted means open: a toast.success raised while it is up is shown here.
  const { done } = usePopupDone(true);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  /* Escape closes, the same as the veil -- the frame every dialog in
     this kit shares. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();

    /* Said against the field rather than thrown as a toast: a toast
       names the problem and then takes it away again, leaving the
       reader to work out which box it meant. */
    const next = {};
    if (!form.classroomNo.trim()) next.classroomNo = "Give the room a name.";
    if (!form.description.trim()) next.description = "Say what is in the room.";
    if (form.outOfOrder && !form.reason.trim()) {
      next.reason = "Say why it is out of order.";
    }
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const payload = {
        classroomNo: form.classroomNo.trim(),
        branch: form.branch,
        floor: form.floor,
        capacity: form.capacity === "" ? null : Number(form.capacity),
        description: form.description.trim(),
        isActive: !form.outOfOrder,
        reason: form.outOfOrder ? form.reason.trim() : "",
        slug: `/${slugify(form.classroomNo)}`,
        date: lab?.date || now,
        startTime: lab?.startTime || now,
        endTime: lab?.endTime || now,
        status: form.outOfOrder ? "inactive" : "active",
      };
      if (lab) {
        await updateClassroom(lab.id, payload);
        toast.success("Classroom updated");
      } else {
        await createClassroom(payload);
        toast.success("Classroom created");
      }
      onSaved();
    } catch (error) {
      toast.error(
        error?.response?.data?.message?.message || "Failed to save classroom",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="pg-modal pg-modal--over"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-form-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--form">
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">Lab / classroom</span>
            <h3 className="pg-modal-title" id="lab-form-title">
              {lab ? "Edit room" : "Add a room"}
            </h3>
            <p className="pg-modal-sub">
              What the room is called, where it is, and how many it seats.
            </p>
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        {/* The form spans body and foot so the footer's button can submit
            it, and the body is the only part that scrolls. Saved, the done
            panel takes the body's place, the footer goes with the form, and
            the popup closes itself after five seconds. */}
        {done ? (
          <div className="pg-modal-body">
            <PopupDone message={done} onClose={onClose} />
          </div>
        ) : (
          <form className="att-form" noValidate onSubmit={submit}>
            <div className="pg-modal-body">
              <div className="mcq-options">
                <div className="pg-field">
                  <label className="pg-label" htmlFor="lab-no">
                    Name
                  </label>
                  <input
                    id="lab-no"
                    className="pg-input"
                    value={form.classroomNo}
                    onChange={(e) => set("classroomNo")(e.target.value)}
                    placeholder="e.g. Lab 53"
                    aria-invalid={Boolean(errors.classroomNo)}
                  />
                  {errors.classroomNo && (
                    <p className="att-error">{errors.classroomNo}</p>
                  )}
                </div>

                <div className="pg-field">
                  <label className="pg-label" htmlFor="lab-branch">
                    Branch
                  </label>
                  <div id="lab-branch">
                    <AsyncPagedSelect
                      value={form.branch}
                      onChange={(id) => set("branch")(id)}
                      fetchPage={(page, search) => getBranches(20, page, search)}
                      toOption={(b) => ({ value: b.id, label: b.name })}
                      initialOption={
                        lab?.branch
                          ? { value: lab.branch, label: lab.branchName || lab.branch }
                          : null
                      }
                      placeholder="Select branch"
                    />
                  </div>
                </div>

                <div className="pg-field">
                  <label className="pg-label" htmlFor="lab-floor">
                    Floor
                  </label>
                  <input
                    id="lab-floor"
                    className="pg-input"
                    value={form.floor}
                    onChange={(e) => set("floor")(e.target.value)}
                    placeholder="e.g. 3rd Floor"
                  />
                </div>

                <div className="pg-field">
                  <label className="pg-label" htmlFor="lab-capacity">
                    Seating capacity
                  </label>
                  <input
                    id="lab-capacity"
                    className="pg-input"
                    type="number"
                    min={0}
                    value={form.capacity}
                    onChange={(e) => set("capacity")(e.target.value)}
                    placeholder="e.g. 30"
                  />
                </div>
              </div>

              <div className="pg-field">
                <label className="pg-label" htmlFor="lab-description">
                  Description
                </label>
                <input
                  id="lab-description"
                  className="pg-input"
                  value={form.description}
                  onChange={(e) => set("description")(e.target.value)}
                  placeholder="e.g. Computer lab · 30 PCs · Projector · AC"
                  aria-invalid={Boolean(errors.description)}
                />
                {errors.description && (
                  <p className="att-error">{errors.description}</p>
                )}
              </div>

              {/* A whole tappable box rather than a bare checkbox: this is
                  the switch that takes a room out of the booking system, so
                  it says what it does underneath itself. */}
              <label className="att-toggle" htmlFor="lab-out">
                <input
                  id="lab-out"
                  type="checkbox"
                  checked={form.outOfOrder}
                  onChange={(e) => set("outOfOrder")(e.target.checked)}
                />
                <span className="att-toggle-text">
                  <b>Out of order</b>
                  <span>The room stays listed but cannot be booked.</span>
                </span>
              </label>

              {form.outOfOrder && (
                <div className="pg-field">
                  <label className="pg-label" htmlFor="lab-reason">
                    Reason
                  </label>
                  <input
                    id="lab-reason"
                    className="pg-input"
                    value={form.reason}
                    onChange={(e) => set("reason")(e.target.value)}
                    placeholder="e.g. Projector failure, AC leakage…"
                    aria-invalid={Boolean(errors.reason)}
                  />
                  {errors.reason && <p className="att-error">{errors.reason}</p>}
                </div>
              )}
            </div>

            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                <button type="button" className="pgm-btn" onClick={onClose}>
                  Cancel
                </button>
              </div>
              <button
                type="submit"
                className="pg-btn pg-btn--main"
                disabled={saving}
              >
                {saving ? "Saving..." : lab ? "Save changes" : "Create room"}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
};

export default LabFormModal;
